import React, { useState, useCallback, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Button from "../../UI/Buttons/Button";
import useApi from "../../../Hooks/useApi";
import showToast from "../../../Helpers/toast";
import {
  changeQuestionOrder,
  deleteQuestion,
} from "../../../Utils/questionnaireUtils";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faBars } from "@fortawesome/free-solid-svg-icons";

const QuestionItem = ({
  question,
  index,
  moveQuestion,
  editQuestion,
  deleteQuestion,
}) => {
  const [, drag] = useDrag({
    type: "QUESTION",
    item: { index, id: question.id, order: question.order },
  });

  const [, drop] = useDrop({
    accept: "QUESTION",
    drop: (draggedItem, monitor) => {
      if (draggedItem.index !== index) {
        moveQuestion(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 border rounded-lg bg-white bg-opacity-20" ref={(node) => drag(drop(node))}>
    <div className="flex items-center gap-2 min-w-[100px] w-auto sm:w-44 sm:border-e-2 sm:border-e-gray-400">
      <span className="text-muted-foreground"> <FontAwesomeIcon icon={faBars} className="px-4 cursor-move" />
</span>
      <span>{question?.type?.replace("_", " ")}</span>
 
    </div>

    <div className="flex-1 min-w-[100px] w-full">
    <Tooltip title={question.name.length>20? question.name: ""} enterTouchDelay={0}>
      <div className="truncate max-w-full">{question.name}</div>
    </Tooltip>
     
    </div>

    <div className="flex items-center gap-2 w-full sm:w-auto">


<Button
              type="iconButton"
              icon="fa-solid fa-pen-to-square"
              backgroundColor="white"
              textColor="green"
              handler={(e) => editQuestion(e, question)}
            />
            <Button
              type="iconButton"
              icon="fa-solid fa-trash"
              backgroundColor="white"
              textColor="red"
              handler={(e) => deleteQuestion(e, question?.id)}
            />
    </div>
  </div>
  );
};

const QuestionListings = ({ list, setter, openQuestionModal }) => {
  const { request } = useApi();
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (list) {
      const sortedQuestions = [...list].sort((a, b) => a.order - b.order);
      setQuestions(sortedQuestions);
    } else {
      setQuestions(list);
    }
  }, [list]);

  const handledeleteQuestion = async (event, question_id) => {
    event.stopPropagation();
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        const response = await deleteQuestion(request, question_id);
        setter(questions.filter((question) => question.id !== question_id));
        showToast({ message: response.message });
      } catch (error) {
        showToast({ message: error?.response?.data?.message, type: "error" });
      }
    }
  };

  const editQuestion = (event, questionData) => {
    event.stopPropagation();
    openQuestionModal(questionData);
  };

  const moveQuestion = useCallback(
    async (dragIndex, hoverIndex) => {
      const draggedQuestion = questions[dragIndex];
      const sectionId = questions[dragIndex].sectionId;

      const updatedQuestions = [...questions];
      updatedQuestions.splice(dragIndex, 1);
      updatedQuestions.splice(hoverIndex, 0, draggedQuestion);

      // Update the order of questions
      const reorderedQuestions = updatedQuestions.map((q, index) => ({
        ...q,
        order: index + 1,
      }));

      setQuestions(reorderedQuestions);

      // Log the changes
      const changedOrders = reorderedQuestions.map((q) => ({
        questionId: q.id,
        order: q.order,
      }));
      // changeQuestionOrder

      try {
        const response = await changeQuestionOrder(request, {
          sectionId: sectionId,
          questions: [...changedOrders],
        });
        setter(response.questions);

        showToast({ message: "Question orders updated successfully" });
      } catch (error) {
        console.log(error);

        showToast({
          message: "Failed to update question orders",
          type: "error",
        });
      }
    },
    [questions, request, setter]
  );

  if (!questions || questions.length <= 0) {
    return <div className="text-center">No Question in this section</div>;
  }

  const isDragDisabled = questions.length <= 1;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-2">
        {questions.map((question, index) => (
          <QuestionItem
            key={question.id}
            question={question}
            index={index}
            moveQuestion={isDragDisabled ? null : moveQuestion}
            editQuestion={editQuestion}
            deleteQuestion={handledeleteQuestion}
          />
        ))}
      </div>
    </DndProvider>
  );
};

export default QuestionListings;
